// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "What Konvo knows" — the person's notes (what the assistant reads before every reply),
// Konvo's summary of them and what the proactive engine learned. Writes update the page at once and
// then re-read it, because the server works out the size and starts a new summary. While the
// summary is being rewritten (summary_stale) the page is read once more a few seconds later.
//
// Other surfaces (the corner "I'll remember that" card) call refresh() after they change a note so
// an open page follows along.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {
	KnowsAddError,
	KnowsHistoryEntry,
	KnowsNote,
	KnowsPage,
	KnowsSource,
	KnowsTopic,
} from '@app/features/konvo_knows/utils/KnowsModel';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('KonvoKnows');

/** How long after a stale summary the page is read again (the summary takes a few seconds). */
const STALE_RETRY_MS = 8000;
/** How long a forgotten note keeps its "Forgotten · Undo" line on the page. */
const UNDO_MS = 8000;

export type KnowsAddResult = {ok: true; note: KnowsNote} | {ok: false; error: KnowsAddError};

class KonvoKnowsStore {
	page: KnowsPage | null = null;
	loading = false;
	loadFailed = false;
	history: Array<KnowsHistoryEntry> | null = null;
	historyLoading = false;
	/** The note being changed in place (desktop); one at a time. */
	editingId: string | null = null;
	/**
	 * Notes forgotten in the last few seconds: the page shows a "Forgotten · Undo" line where they
	 * were. Inline rather than a toast, because toasts sit under the Settings modal's inert layer.
	 */
	recentlyForgotten: Array<KnowsNote> = [];
	private staleTimer: ReturnType<typeof setTimeout> | null = null;
	private staleRetried = false;
	private loadSeq = 0;
	private undoTimers = new Map<string, ReturnType<typeof setTimeout>>();

	constructor() {
		makeAutoObservable<KonvoKnowsStore, 'staleTimer' | 'staleRetried' | 'loadSeq' | 'undoTimers'>(
			this,
			{staleTimer: false, staleRetried: false, loadSeq: false, undoTimers: false},
			{autoBind: true},
		);
	}

	get noteCount(): number | null {
		return this.page ? this.page.notes.length : null;
	}

	/** Reads the page. Safe to call often; the newest answer wins. */
	async load(): Promise<void> {
		const seq = ++this.loadSeq;
		this.loading = this.page == null;
		try {
			const response = await http.get<KnowsPage>(Endpoints.KONVO_ASSISTANT_KNOWS);
			if (seq !== this.loadSeq || !response.body) return;
			this.applyPage(response.body);
		} catch (error) {
			logger.warn('Failed to load what Konvo knows', error);
			runInAction(() => {
				if (seq === this.loadSeq) this.loadFailed = this.page == null;
			});
		} finally {
			runInAction(() => {
				if (seq === this.loadSeq) this.loading = false;
			});
		}
	}

	/** For other surfaces that changed a note: re-read the page (and history, if it was open). */
	refresh(): void {
		void this.load();
		if (this.history) void this.loadHistory();
	}

	private applyPage(page: KnowsPage): void {
		this.page = page;
		this.loadFailed = false;
		if (!page.summary_stale) {
			this.staleRetried = false;
			return;
		}
		// One more look once Konvo has had time to write the new summary; not a poll.
		if (this.staleRetried || this.staleTimer) return;
		this.staleRetried = true;
		this.staleTimer = setTimeout(() => {
			this.staleTimer = null;
			void this.load();
		}, STALE_RETRY_MS);
	}

	/** "Tell Konvo something to remember"; also the Undo of a forget (pass topic and source back). */
	async add(text: string, options: {topic?: KnowsTopic | null; source?: KnowsSource} = {}): Promise<KnowsAddResult> {
		const trimmed = text.trim();
		if (!trimmed) return {ok: false, error: 'empty'};
		try {
			const response = await http.post<{note: KnowsNote}>(Endpoints.KONVO_ASSISTANT_KNOWS_NOTES, {
				body: {text: trimmed, topic: options.topic ?? undefined, source: options.source ?? undefined},
			});
			const note = response.body?.note;
			if (!note) return {ok: false, error: 'failed'};
			runInAction(() => {
				if (this.page && !this.page.notes.some((existing) => existing.id === note.id)) {
					this.page.notes = [...this.page.notes, note];
				}
			});
			this.afterWrite();
			return {ok: true, note};
		} catch (error) {
			if (error instanceof HttpError) {
				const code = (error.body as {error?: unknown} | null)?.error;
				if (code === 'empty' || code === 'duplicate' || code === 'full') return {ok: false, error: code};
			}
			logger.warn('Failed to add a note', error);
			return {ok: false, error: 'failed'};
		}
	}

	/** Changes a note's text and/or topic. Resolves false when it did not save. */
	async update(id: string, change: {text?: string; topic?: KnowsTopic | null}): Promise<boolean> {
		const before = this.page?.notes.find((note) => note.id === id) ?? null;
		const body: {text?: string; topic?: KnowsTopic | null} = {};
		if (change.text !== undefined) {
			const text = change.text.trim();
			if (!text) return false;
			body.text = text;
		}
		if (change.topic !== undefined) body.topic = change.topic;
		this.replaceNote(id, (note) => ({...note, ...body}));
		try {
			const response = await http.patch<{note: KnowsNote}>(Endpoints.KONVO_ASSISTANT_KNOWS_NOTE(id), {body});
			const saved = response.body?.note;
			if (saved) this.replaceNote(id, () => saved);
			this.afterWrite();
			return true;
		} catch (error) {
			logger.warn('Failed to change a note', error);
			if (before) this.replaceNote(id, () => before);
			return false;
		}
	}

	setEditing(id: string | null): void {
		this.editingId = id;
	}

	/** Forgets one note straight away. Resolves the note (for Undo), or null when it failed. */
	async forget(id: string): Promise<KnowsNote | null> {
		const note = this.page?.notes.find((candidate) => candidate.id === id) ?? null;
		if (!note) return null;
		runInAction(() => {
			if (this.page) this.page.notes = this.page.notes.filter((candidate) => candidate.id !== id);
			if (this.editingId === id) this.editingId = null;
			this.recentlyForgotten = [...this.recentlyForgotten, note];
		});
		this.undoTimers.set(
			id,
			setTimeout(() => this.dropForgotten(id), UNDO_MS),
		);
		try {
			await http.delete(Endpoints.KONVO_ASSISTANT_KNOWS_NOTE(id));
			this.afterWrite();
			return note;
		} catch (error) {
			logger.warn('Failed to forget a note', error);
			this.dropForgotten(id);
			void this.load();
			return null;
		}
	}

	/** Undo of a forget: the same text back, in the same topic, with its source. */
	async restore(note: Pick<KnowsNote, 'id' | 'text' | 'topic' | 'source'>): Promise<KnowsAddResult> {
		this.dropForgotten(note.id);
		return this.add(note.text, {topic: note.topic, source: note.source});
	}

	private dropForgotten(id: string): void {
		const timer = this.undoTimers.get(id);
		if (timer) clearTimeout(timer);
		this.undoTimers.delete(id);
		this.recentlyForgotten = this.recentlyForgotten.filter((note) => note.id !== id);
	}

	/** Forgets every note (what was learned stays). */
	async forgetAll(): Promise<boolean> {
		try {
			await http.delete(Endpoints.KONVO_ASSISTANT_KNOWS);
			runInAction(() => {
				if (this.page) this.page = {...this.page, notes: [], summary: null, summary_stale: false, size: 0};
			});
			this.afterWrite();
			return true;
		} catch (error) {
			logger.warn('Failed to forget everything', error);
			return false;
		}
	}

	/** Reset a trigger's tuning or unmute a topic; the server answers with the whole page. */
	async undoLearned(id: string): Promise<boolean> {
		try {
			const response = await http.post<KnowsPage>(Endpoints.KONVO_ASSISTANT_KNOWS_LEARNED_UNDO(id));
			if (response.body) this.applyPage(response.body);
			return true;
		} catch (error) {
			logger.warn('Failed to undo something Konvo learned', error);
			return false;
		}
	}

	async loadHistory(): Promise<void> {
		this.historyLoading = this.history == null;
		try {
			const response = await http.get<{history: Array<KnowsHistoryEntry>}>(Endpoints.KONVO_ASSISTANT_KNOWS_HISTORY);
			runInAction(() => {
				this.history = response.body?.history ?? [];
			});
		} catch (error) {
			logger.warn('Failed to load the history of what Konvo knows', error);
			runInAction(() => {
				this.history ??= [];
			});
		} finally {
			runInAction(() => {
				this.historyLoading = false;
			});
		}
	}

	private replaceNote(id: string, next: (note: KnowsNote) => KnowsNote): void {
		if (!this.page) return;
		this.page.notes = this.page.notes.map((note) => (note.id === id ? next(note) : note));
	}

	/** The server owns size, summary and history: read them again after any write. */
	private afterWrite(): void {
		this.staleRetried = false;
		void this.load();
		if (this.history) void this.loadHistory();
	}
}

const KonvoKnows = new KonvoKnowsStore();

export default KonvoKnows;
